import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import backgroundImage from "./HeaderSlider_04.webp";
import useScrollToTop from "./../../hooks/useScollToTop";
import { registerUser, checkEmailExists } from "../../services/userServices";
import { sendNotificationToAdmin } from "../../services/notificationServices";
import Modal from "../../hooks/Modal";
import Button from "../../components/Button/Button";

/* ================== Brand (comme ton Login MonAppliAG) ================== */
const BRAND = {
  blue: "#013E8E",
  green: "#4CA82F",
  blueLight: "#038DE6",
};

/* ================== Heuristiques “vrai nom” (front only) ================== */
/** Autorise lettres (avec accents), espaces, tirets, apostrophes. */
const NAME_ALLOWED = /^[\p{L}\p{M}][\p{L}\p{M}'\- ]{0,48}$/u; // 1–49 car.
const VOWELS = "AEIOUYÀÂÄÆÉÈÊËÎÏÔÖÙÛÜŸaeiouyàâäæéèêëîïôöùûüÿ";

const looksLikeHumanName = (raw) => {
  const s = String(raw || "").trim();

  if (s.length < 2 || s.length > 50)
    return { ok: false, reason: "2 à 50 caractères attendus." };

  if (!NAME_ALLOWED.test(s))
    return {
      ok: false,
      reason: "Caractères autorisés : lettres, espace, - et '.",
    };

  if (/[0-9_@#$%^&*~`+=/\\|<>{}[\]"]/u.test(s))
    return { ok: false, reason: "Pas de chiffres ni de symboles spéciaux." };

  const sepCount = (s.match(/[-']/g) || []).length;
  if (sepCount > 3) return { ok: false, reason: "Trop de séparateurs (- ')." };

  if (!Array.from(s).some((c) => VOWELS.includes(c)))
    return { ok: false, reason: "Le nom doit contenir au moins une voyelle." };

  if (/[bcdfghjklmnpqrstvwxyz]{5,}/i.test(s))
    return { ok: false, reason: "Suite de consonnes trop longue." };

  if (/(.)\1\1\1/u.test(s))
    return { ok: false, reason: "Répétition de caractère anormale." };

  let switches = 0;
  for (let i = 1; i < s.length; i++) {
    if (/[A-Za-z]/.test(s[i]) && /[A-Za-z]/.test(s[i - 1])) {
      if (
        (s[i] === s[i].toUpperCase()) !==
        (s[i - 1] === s[i - 1].toUpperCase())
      )
        switches++;
    }
  }
  if (switches > 4) return { ok: false, reason: "Casse irrégulière." };

  const tokens = s.split(/[\s\-']+/).filter(Boolean);
  for (const t of tokens) {
    if (t.length > 2 && !Array.from(t).some((c) => VOWELS.includes(c))) {
      return { ok: false, reason: "Chaque partie du nom doit être plausible." };
    }
  }

  return { ok: true };
};

const normalizePrenom = (s) =>
  s
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .split(/([\-'\s])/)
    .map((part) =>
      /^[-'\s]$/.test(part)
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join("");

/* ================== Page ================== */
function RegisterPage() {
  useScrollToTop();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");

  const [errorMessage, setErrorMessage] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Validations live (front)
  const nomVal = useMemo(() => looksLikeHumanName(nom), [nom]);
  const prenomVal = useMemo(() => looksLikeHumanName(prenom), [prenom]);

  const emailOk = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
    [email],
  );
  const pwdOk = useMemo(() => password.length >= 10, [password]);

  const formOk =
    nomVal.ok &&
    prenomVal.ok &&
    emailOk &&
    pwdOk &&
    !checkingEmail &&
    !submitting;

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleResetPassword = () => {
    closeModal();
    navigate("/forgot-password");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!nomVal.ok) {
      setErrorMessage(`Nom invalide : ${nomVal.reason || ""}`.trim());
      return;
    }
    if (!prenomVal.ok) {
      setErrorMessage(`Prénom invalide : ${prenomVal.reason || ""}`.trim());
      return;
    }
    if (!emailOk) {
      setErrorMessage("Email invalide.");
      return;
    }
    if (!pwdOk) {
      setErrorMessage("Mot de passe : minimum 10 caractères.");
      return;
    }

    setCheckingEmail(true);
    const cleanEmail = email.trim().toLowerCase();
    const emailCheck = await checkEmailExists(cleanEmail);
    setCheckingEmail(false);

    if (emailCheck?.error) {
      setErrorMessage(emailCheck.error);
      return;
    }
    if (emailCheck?.exists) {
      openModal();
      return;
    }

    setSubmitting(true);
    try {
      const result = await registerUser(
        nom.trim(),
        normalizePrenom(prenom),
        cleanEmail,
        password,
      );

      if (result?.error) {
        setErrorMessage(result.error);
        return;
      }

      // notif admin
      const token = localStorage.getItem("token");
      const message = `Un nouvel utilisateur a été créé : ${nom.trim()} ${normalizePrenom(
        prenom,
      )} (${cleanEmail})`;

      try {
        await sendNotificationToAdmin(message, token);
      } catch {}

      navigate("/login");
    } catch (err) {
      console.error(err);
      setErrorMessage("Inscription impossible pour le moment.");
    } finally {
      setSubmitting(false);
    }
  };

  // Helpers UI
  const inputBase =
    "w-full rounded-xl border bg-white px-4 py-3 outline-none transition";
  const focusRing =
    "focus:ring-2 focus:ring-[#013E8E]/25 focus:border-[#013E8E]";
  const inputBorderOk = "border-slate-200";
  const inputBorderErr =
    "border-rose-300 focus:ring-rose-300/30 focus:border-rose-500";

  return (
    <div
      className="min-h-screen relative flex items-center justify-center px-4 py-10"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* overlay */}
      <div className="absolute inset-0 bg-black/55" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(circle at top, rgba(1,62,142,0.55), transparent 55%), radial-gradient(circle at bottom, rgba(76,168,47,0.55), transparent 55%)",
        }}
      />

      <div className="relative w-full max-w-md">
        <div className="bg-white/50 backdrop-blur-md border border-white/50 shadow-2xl rounded-2xl p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-6">
            <img
              src="/img/logos/logo_monappliclub.png"
              alt="MonAppliClub"
              className="h-14 md:h-16 mx-auto mb-3 select-none"
              draggable="false"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
              Inscription
            </h1>
            <p className="text-sm text-slate-700 mt-2">
              Créez votre compte pour accéder aux services du club
            </p>
          </div>

          {/* Erreur globale */}
          {errorMessage && (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* NOM */}
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Nom
              </label>
              <input
                type="text"
                placeholder="Votre nom"
                className={`${inputBase} ${
                  nom.length && !nomVal.ok ? inputBorderErr : inputBorderOk
                } ${focusRing}`}
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                onBlur={() => setNom(nom.trim())}
                required
                autoComplete="family-name"
              />
              {nom.length > 0 && !nomVal.ok && (
                <p className="mt-1 text-xs text-rose-700">{nomVal.reason}</p>
              )}
            </div>

            {/* PRÉNOM */}
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Prénom
              </label>
              <input
                type="text"
                placeholder="Votre prénom"
                className={`${inputBase} ${
                  prenom.length && !prenomVal.ok
                    ? inputBorderErr
                    : inputBorderOk
                } ${focusRing}`}
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                onBlur={() => setPrenom(normalizePrenom(prenom))}
                required
                autoComplete="given-name"
              />
              {prenom.length > 0 && !prenomVal.ok && (
                <p className="mt-1 text-xs text-rose-700">{prenomVal.reason}</p>
              )}
            </div>

            {/* EMAIL */}
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Email
              </label>
              <input
                type="email"
                placeholder="Votre email"
                className={`${inputBase} ${
                  email.length && !emailOk ? inputBorderErr : inputBorderOk
                } ${focusRing}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              {email.length > 0 && !emailOk && (
                <p className="mt-1 text-xs text-rose-700">Email invalide.</p>
              )}
            </div>

            {/* MOT DE PASSE + toggle texte */}
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Mot de passe
              </label>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Choisissez un mot de passe"
                  className={`${inputBase} pr-28 ${
                    password.length && !pwdOk ? inputBorderErr : inputBorderOk
                  } ${focusRing}`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-3 py-2
                             text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
                  aria-label={
                    showPassword
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                >
                  {showPassword ? "Masquer" : "Afficher"}
                </button>
              </div>

              <div className="mt-1 flex items-center justify-between text-xs">
                {!pwdOk && password.length > 0 ? (
                  <span className="text-rose-700">Minimum 10 caractères.</span>
                ) : (
                  <span className="text-slate-600">Minimum 10 caractères.</span>
                )}

                {(checkingEmail || submitting) && (
                  <span className="text-slate-600">
                    {submitting ? "Inscription..." : "Vérification email..."}
                  </span>
                )}
              </div>
            </div>

            {/* CTA gradient */}
            <button
              type="submit"
              disabled={!formOk}
              className={`
                w-full rounded-xl px-4 py-3 font-semibold shadow-sm transition border
                ${!formOk ? "cursor-not-allowed opacity-70" : "hover:opacity-95"}
              `}
              style={{
                borderColor: BRAND.blue,
                color: "white",
                backgroundImage: `linear-gradient(90deg, ${BRAND.blue}, ${BRAND.green})`,
              }}
            >
              {submitting
                ? "Inscription..."
                : checkingEmail
                  ? "Vérification email..."
                  : "S'inscrire"}
            </button>

            {/* liens */}
            <div className="flex items-center justify-between pt-2 text-sm">
              <Link
                to="/login"
                className="hover:underline"
                style={{ color: BRAND.blue }}
              >
                Déjà un compte ? Se connecter
              </Link>

              <Link
                to="/forgot-password"
                className="text-slate-700 hover:underline"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </form>
        </div>

        <p className="text-center text-xs text-white/85 mt-4">
          MonAppliClub • Gestion de club simplifiée
        </p>
      </div>

      {/* Modal email existant */}
      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          <div className="space-y-4">
            <p className="text-sm">
              Le mail est déjà enregistré. Souhaitez-vous réinitialiser votre
              mot de passe ?
            </p>
            <div className="flex justify-center gap-4">
              <Button text="Oui" onClick={handleResetPassword} />
              <Button text="Non" onClick={closeModal} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default RegisterPage;
